import React, { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ConfigureView, ZoomDirection, ZoomRequest } from 'src/generated-types/configure-positions'
import { ZoomAnchor, computeWheelZoom, computeZoomAnchor } from './drawingGeometry.action'

const PAN_MOVE_THRESHOLD_PX = 3

interface PanState {
  startX: number
  startY: number
  scrollLeft: number
  scrollTop: number
  moved: boolean
}

interface UseDrawingPanZoomOptions {
  view: ConfigureView
  zoom: number
  onZoom: (request: ZoomRequest) => void
  hostRef: RefObject<HTMLDivElement>
  viewportRef: RefObject<HTMLDivElement>
}

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

export const useDrawingPanZoom = ({ view, zoom, onZoom, hostRef, viewportRef }: UseDrawingPanZoomOptions) => {
  const zoomRef = useRef(zoom)
  const anchorRef = useRef<ZoomAnchor | null>(null)
  const panRef = useRef<PanState | null>(null)
  const spaceHeldRef = useRef(false)
  const pointerOverRef = useRef(false)
  const [isHandMode, setHandMode] = useState(false)
  const [isPanning, setPanning] = useState(false)
  zoomRef.current = zoom

  const captureAnchor = useCallback(
    (clientX: number, clientY: number) => {
      const host = hostRef.current
      const viewport = viewportRef.current
      if (!host?.offsetWidth || !viewport) return
      anchorRef.current = computeZoomAnchor(
        clientX,
        clientY,
        host.getBoundingClientRect(),
        viewport.getBoundingClientRect(),
      )
    },
    [hostRef, viewportRef],
  )

  useLayoutEffect(() => {
    const anchor = anchorRef.current
    const host = hostRef.current
    const viewport = viewportRef.current
    if (!anchor || !host || !viewport) return
    anchorRef.current = null
    viewport.scrollLeft = host.offsetLeft + anchor.ratioX * host.offsetWidth - anchor.offsetX
    viewport.scrollTop = host.offsetTop + anchor.ratioY * host.offsetHeight - anchor.offsetY
  }, [zoom, hostRef, viewportRef])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const onWheel = (event: WheelEvent) => {
      if (view !== 'drawing') return
      const host = hostRef.current
      if (!host?.offsetWidth || !host.offsetHeight) return
      event.preventDefault()
      captureAnchor(event.clientX, event.clientY)
      zoomRef.current = computeWheelZoom(zoomRef.current, event.deltaY, event.deltaMode, viewport.clientHeight)
      onZoom(zoomRef.current)
    }
    viewport.addEventListener('wheel', onWheel, { passive: false })
    return () => viewport.removeEventListener('wheel', onWheel)
  }, [view, onZoom, captureAnchor, hostRef, viewportRef])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || event.repeat || isTypingTarget(event.target) || !pointerOverRef.current) return
      event.preventDefault()
      if (document.activeElement instanceof HTMLButtonElement) document.activeElement.blur()
      spaceHeldRef.current = true
      setHandMode(true)
    }
    const releaseSpace = () => {
      spaceHeldRef.current = false
      setHandMode(false)
    }
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code !== 'Space') return
      if (spaceHeldRef.current) event.preventDefault()
      releaseSpace()
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', releaseSpace)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', releaseSpace)
    }
  }, [])

  const zoomAt = (clientX: number, clientY: number, direction: ZoomDirection) => {
    if (direction === 'fit') {
      anchorRef.current = null
      onZoom('fit')
      viewportRef.current?.scrollTo({ left: 0, top: 0 })
      return
    }
    captureAnchor(clientX, clientY)
    onZoom(direction)
  }

  const zoomFromCenter = (direction: ZoomDirection) => {
    const viewportRect = viewportRef.current?.getBoundingClientRect()
    if (viewportRect)
      zoomAt(viewportRect.left + viewportRect.width / 2, viewportRect.top + viewportRect.height / 2, direction)
    else onZoom(direction)
  }

  const isPanGesture = (event: React.PointerEvent) =>
    event.button === 1 || event.button === 2 || (event.button === 0 && spaceHeldRef.current)

  const startPan = (event: React.PointerEvent) => {
    if (!isPanGesture(event)) return false
    const viewport = viewportRef.current
    if (!viewport) return true
    panRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop,
      moved: false,
    }
    viewport.setPointerCapture(event.pointerId)
    setPanning(true)
    event.preventDefault()
    return true
  }

  const movePan = (event: React.PointerEvent) => {
    const pan = panRef.current
    const viewport = viewportRef.current
    if (!pan || !viewport) return false
    const deltaX = event.clientX - pan.startX
    const deltaY = event.clientY - pan.startY
    if (Math.hypot(deltaX, deltaY) > PAN_MOVE_THRESHOLD_PX) pan.moved = true
    viewport.scrollLeft = pan.scrollLeft - deltaX
    viewport.scrollTop = pan.scrollTop - deltaY
    return true
  }

  const endPan = () => {
    const pan = panRef.current
    if (!pan) return null
    panRef.current = null
    setPanning(false)
    return { moved: pan.moved }
  }

  const onPointerEnter = () => {
    if (view === 'drawing') pointerOverRef.current = true
  }

  const onPointerLeave = () => {
    pointerOverRef.current = false
    if (!spaceHeldRef.current) setHandMode(false)
  }

  return {
    isHandMode,
    isPanning,
    spaceHeldRef,
    zoomAt,
    zoomFromCenter,
    startPan,
    movePan,
    endPan,
    onPointerEnter,
    onPointerLeave,
  }
}
